import { Store } from "vuex";
import { openDeepLink, parseDeepLink } from '@common/deepLink/deepLinkHelper';
import { DeepLinkInterface, DeepLinkTypes } from '@common/deepLink/types';
import VueRouter, { Route } from "vue-router";
import { Element } from "@api/models/Element";
import { RepositoryFabric } from "@api/repositories/RepositoryFabric";
import { EventBus } from "@common/EventBus";
import { mayAttendToSession } from "@common/AgendaHelper";
import { Invite } from "@api/models/meetings/Invite";
import { MeetingRepository } from "@api/repositories/MeetingRepository";
import { ObjectRepository } from "@api/repositories/ObjectRepository";
import { DeepLink } from "@common/deepLink/DeepLink";

export interface DeepLinkOpenParams {
    source: string | DeepLink;
    backend: string;
    router: VueRouter;
    route: Route;
}
export class NotificationLinkHelper {

    constructor(private store: Store<any>, private repositories: RepositoryFabric) {}

    public async openDeepLink({ source, backend, router, route }: DeepLinkOpenParams): Promise<void> {

        const deepLink = typeof source === "string" ? parseDeepLink(source) : source;

        if (deepLink.data?.id) {

            //USERPROFILE:
            if (deepLink.type == DeepLinkTypes.userProfile) {
                EventBus.$emit("showChatByProfileId", deepLink.data.id);
                return;
            }

            const eventId = await this.getEventId(deepLink, route);
            const isEventAvailable = this.checkIsEventAvailable(eventId, deepLink);

            if (eventId && isEventAvailable) {
                openDeepLink({ deepLink, backend, eventId }, router);
                EventBus.$emit('notification-opened');
            }
        }
        else if(deepLink.data?.url) {
            window.open(deepLink.data.url);
        }
    }

    /**
     * Returns event available or not
     * @param deepLink DeepLink
     * @param route VeuRouter route
     * @returns Boolean - event available or not
     */
    private checkIsEventAvailable(eventId: string, deepLink: DeepLink): boolean {

        if (eventId) {
            //проверка на наличия мероприятия в списке
            const eventAvailable = this.store.state.events?.list.some((event: Element) => event.id === eventId);

            if (eventAvailable) {
                if (deepLink.type === DeepLinkTypes.catalog) {
                    const catalogs = this.store.getters['events/selectedEventViewParam']('catalog', null);

                    //catalog schemaId not allowed
                    if (!catalogs.some((catalog: any) => catalog.schemaId === deepLink.data?.schemaId)) {
                        console.warn("catalog not exist in target event");
                        return false;
                    }
                }

                return true;
            }
        }
        else {
            console.warn("eventId is empty");
        }

        return false;
    }

    /**
     * Returns eventId for object in deeplink
     * @param deepLink link object
     * @param route vue-router route
     * @returns eventId
     */
    private async getEventId(deepLink: DeepLinkInterface, route: Route): Promise<string> {

        if (deepLink.type == DeepLinkTypes.event) {
            return deepLink.data.id.toString()
        }

        if (deepLink.type == DeepLinkTypes.htmlPage) {
            const infoParams = this.store.getters['events/selectedEventViewParam']('info', null);
            return infoParams ? route.params.eventId : "";
        }

        if (deepLink.type == DeepLinkTypes.session) {
            const alreadyOpened = route.name === "ViewSession" && route.params?.id === deepLink.data.id
            return this.tryToFindSessionEventId(deepLink.data.id, deepLink.data.schemaId, alreadyOpened);
        }

        if (deepLink.type == DeepLinkTypes.meeting) {
            return await this.tryToFindMeetingEventId(deepLink.data.id);
        }

        if (deepLink.type == DeepLinkTypes.catalog) {
            return await this.getPartnerEventId(deepLink.data.id, deepLink.data.schemaId);
        }

        return ""
    }

    private async tryToFindSessionEventId(sessionId: string, schemaId: string, alreadyOpened: boolean): Promise<string> {

        let eventId = "";

        const programs = this.store.state.program.programs;
        const speakerOrAdmin = (this.store.getters['isAdmin'] || this.store.getters['isSpeaker']);

        //finding eventId from sessions
        for (const programDate in programs) {
            if (Object.prototype.hasOwnProperty.call(programs, programDate) && !eventId) {

                const agendas = programs[programDate].allFutureAgendas;

                const agenda = agendas.find((agenda: Element) => agenda.id === sessionId);

                if (agenda) {
                    if (mayAttendToSession(agenda, speakerOrAdmin, alreadyOpened)) {
                        eventId = programs[programDate].eventId;
                    }
                }
            }
        }

        //если в текущем мероприятии сессия не найдена
        if(!eventId) {
            try {
                const objectRepository = this.repositories.get(RepositoryFabric.OBJECT) as ObjectRepository;

                const [agenda] = await objectRepository.query(schemaId, {
                    where: {
                        id: sessionId,
                        ...this.store.getters['groupFilter']
                    }
                });

                if (agenda && mayAttendToSession(agenda, speakerOrAdmin, alreadyOpened)) {
                    eventId = agenda.eventId;
                }
            }
            catch (e) {
                console.error(e)
            }
        }

        return eventId
    }

    private async tryToFindMeetingEventId(meetingId: string): Promise<string> {
        let eventId = "";

        //список будет только если было открыто окно с приглашниями
        const invites = this.store.state.invites.dataHelper?.invites as Invite[] | undefined;

        if(invites) {
            eventId = invites.find((invite) => invite.meetingId === meetingId)?.eventId ?? "";
        }

        if(!eventId) {

            try {
                const meetingRepository = this.repositories.get(RepositoryFabric.MEETING) as MeetingRepository;
                eventId = (await meetingRepository.find(meetingId)).eventId ?? "";
            }
            catch(e) {
                eventId = "";
            }
        }

        return eventId;
    }

    private async getPartnerEventId(partnerId: string, schemaId: string): Promise<string> {

        if(!partnerId || !schemaId) {
            return ""
        }

        try {
            const objectRepository = this.repositories.get(RepositoryFabric.OBJECT) as ObjectRepository;

            const [partner] = await objectRepository.query(schemaId, {
                where: {
                    id: partnerId,
                    ...this.store.getters['groupFilter']
                }
            });

            //если партнер не привязан к мероприятию, то открывать
            if (partner && !partner.eventId) {
                return this.store.state.events.selected;
            }

            return partner.eventId
        }
        catch(e) {
            return ""
        }
    }
}
